"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, ShieldCheck, Clock, ArrowRight, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function HeroWashingtonDC() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const quickFacts = [{
    icon: Clock,
    label: "24/7 response",
    detail: "Direct local crews on call day and night"
  }, {
    icon: ShieldCheck,
    label: "Direct billing",
    detail: "We coordinate straight with your insurance"
  }, {
    icon: MapPin,
    label: "All quadrants",
    detail: "NW, NE, SW, and SE rapid dispatch"
  }];
  return <section id="herowashingtondc" data-nav="dark" className="relative overflow-hidden bg-secondary text-secondary-foreground py-20 md:py-28 lg:py-32">
      {/* Subtle ambient lighting effects */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -bottom-40 right-0 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="mx-auto max-w-4xl text-center">
          {/* Tag badge */}
          <motion.div variants={itemVariants} className="mb-6 flex justify-center">
            <Badge variant="outline" className="border-secondary-foreground/20 bg-secondary/80 px-3.5 py-1 text-xs font-medium text-secondary-foreground tracking-wide">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent" />
              District of Columbia Dispatch
            </Badge>
          </motion.div>

          {/* Headline */}
          <motion.h1 variants={itemVariants} className="font-default text-4xl font-bold tracking-tight text-secondary-foreground sm:text-5xl lg:text-6xl">
            Restoration services in Washington DC
          </motion.h1>

          {/* Subheadline */}
          <motion.p variants={itemVariants} className="mx-auto mt-6 max-w-2xl text-lg text-secondary-foreground/80 sm:text-xl">
            24/7 local emergency response across the District.
          </motion.p>

          {/* CTAs */}
          <motion.div variants={itemVariants} className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-semibold">
              <Link to="/ContactRequestServicePage" className="inline-flex items-center gap-2">
                <PhoneCall className="h-4 w-4" />
                <span>Request immediate dispatch</span>
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-secondary-foreground/20 bg-secondary/40 text-secondary-foreground hover:bg-secondary/70 hover:text-secondary-foreground">
              <Link to="/WaterDamageFloodRestorationPage" className="inline-flex items-center gap-2">
                <span>View DC coverage</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </motion.div>

          {/* Key Facts / Highlights */}
          <motion.div variants={itemVariants} className="mt-16 grid grid-cols-1 gap-4 text-left sm:grid-cols-3 sm:gap-6 pt-10 border-t border-secondary-foreground/10">
            {quickFacts.map((fact, index) => {
            const Icon = fact.icon;
            return <div key={index} data-index={index} className="rounded-lg border border-secondary-foreground/10 bg-secondary/50 p-4 transition-[border-color,background-color] hover:border-secondary-foreground/20 hover:bg-secondary/70">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/20 text-primary-foreground">
                      <Icon className="h-5 w-5 text-accent" />
                    </div>
                    <div>
                      <p className="font-default text-sm font-semibold text-secondary-foreground">
                        {fact.label}
                      </p>
                      <p className="text-xs text-secondary-foreground/75">
                        {fact.detail}
                      </p>
                    </div>
                  </div>
                </div>;
          })}
          </motion.div>
        </motion.div>
      </div>
    </section>;
}